// SCR-009 (commercial terms: assignments, trials, burn-down) and SCR-010 (usage adjustments) view
// model — REQ-035, REQ-036, REQ-037. Approval is available to any `billing:approve` holder,
// including the adjustment's own creator (user decision 2026-09-24 #3): `canApproveAdjustment`
// below takes no actor/subject argument at all, so it structurally cannot hide the button from a
// creator. Every adjustment row keeps its audited actor (createdBy/approvedBy/voidedBy) and reason
// on screen. Money only from `ui/money.ts`.
import type { ApiClient } from "@nexus/ui";
import type { ApiError } from "@nexus/ui";
import type { components } from "../../api/schema";
import type { Me } from "../../session/session";
import { type FieldErrors, integerBetween, lengthBetween, month, usdAmount } from "@nexus/ui";
import { actorDisplay, knownActor } from "../../ui/identity";
import { bpToPercentInput, money, parseUsd, type MoneyDisplay } from "@nexus/ui";
import { longMonthLabel, monthLabel } from "@nexus/ui";

/** Who is signed in, for "You" in the by-columns (identity.ts's `actorDisplay`). */
export type Viewer = Pick<Me, "subject"> | null | undefined;

export type PlanAssignment = components["schemas"]["PlanAssignment"];
export type Trial = components["schemas"]["Trial"];
export type TrialBounds = components["schemas"]["TrialBounds"];
export type TrialStatus = components["schemas"]["TrialStatus"];
export type TrialConsumption = components["schemas"]["TrialConsumption"];
export type Burndown = components["schemas"]["Burndown"];
export type BurndownDay = components["schemas"]["BurndownDay"];
export type UsageAdjustment = components["schemas"]["UsageAdjustment"];
export type UsageAdjustmentKind = components["schemas"]["UsageAdjustmentKind"];
export type UsageAdjustmentStatus = components["schemas"]["UsageAdjustmentStatus"];
export type CreateTrialRequest = components["schemas"]["CreateTrialRequest"];
export type CreateUsageAdjustmentRequest = components["schemas"]["CreateUsageAdjustmentRequest"];
export type TokenClass = components["schemas"]["TokenClass"];
export type OrgBillingSettings = components["schemas"]["OrgBillingSettings"];
export type OrgBillingSettingsEntry = components["schemas"]["OrgBillingSettingsEntry"];
export type BillingMode = components["schemas"]["BillingMode"];
export type AwsPaidBy = components["schemas"]["AwsPaidBy"];
export type PutOrgBillingSettingsRequest = components["schemas"]["PutOrgBillingSettingsRequest"];

export const TOKEN_CLASS_OPTIONS: readonly TokenClass[] = ["input", "output", "cache_read", "cache_write_5m", "cache_write_1h"];
export const ADJUSTMENT_KIND_OPTIONS: readonly UsageAdjustmentKind[] = ["credit", "write_off", "corrected_tokens"];

function capitalize(word: string): string {
  return word.length === 0 ? word : word[0]!.toUpperCase() + word.slice(1);
}

// ------------------------------------------------------------------------ org billing mode (SCR-004/009, REQ-051)

/** SCR-009 shows plan/trial controls only for months the org is `billed`; otherwise "Not billed — proxy only". */
export function isBilled(settings: OrgBillingSettings): boolean {
  return settings.billingMode === "billed";
}

export const BILLING_MODE_OPTIONS: readonly BillingMode[] = ["not_billed", "billed"];
export const AWS_PAID_BY_OPTIONS: readonly AwsPaidBy[] = ["client", "allcode"];

export const NOT_BILLED_MESSAGE = "Not billed — proxy only.";

export interface BillingModeScheduleRow {
  effectiveMonth: string;
  billingModeLabel: string;
  awsPaidByLabel: string;
  createdBy: string;
  createdAt: string;
}

export function billingModeScheduleRow(entry: OrgBillingSettingsEntry, viewer?: Viewer): BillingModeScheduleRow {
  return {
    effectiveMonth: entry.effectiveMonth,
    billingModeLabel: entry.billingMode === "billed" ? "Billed" : "Not billed",
    awsPaidByLabel: entry.awsPaidBy === "allcode" ? "AllCode" : "Customer",
    createdBy: actorDisplay(entry.createdBy, viewer, knownActor(entry, "createdBy")),
    createdAt: entry.createdAt,
  };
}

export function compareScheduleMonth(a: BillingModeScheduleRow, b: BillingModeScheduleRow): number {
  return b.effectiveMonth.localeCompare(a.effectiveMonth);
}

export interface BillingModeFormValues {
  effectiveMonth: string;
  billingMode: BillingMode;
  awsPaidBy: AwsPaidBy;
  planId: string;
}

/** New orgs read `not_billed` / `client` with no schedule entry (REQ-051) — the form starts there,
 *  from the given month (the month picker always holds a month; the current one by default). */
export function emptyBillingModeForm(settings: OrgBillingSettings, effectiveMonth = ""): BillingModeFormValues {
  return { effectiveMonth, billingMode: settings.billingMode, awsPaidBy: settings.awsPaidBy, planId: "" };
}

const PLAN_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Client-side shape checks only; `rate_plan_required`/`period_closed`/`plan_not_active` are the
 *  server's calls to make (SCR-004: "shows `rate_plan_required` inline… `period_closed`…"). */
export function validateBillingModeForm(values: BillingModeFormValues): FieldErrors {
  const errors: FieldErrors = {};
  const monthError = month(values.effectiveMonth);
  if (monthError) errors.effectiveMonth = monthError;
  if (values.billingMode === "billed") {
    if (values.planId.trim() === "") errors.planId = "Choose a plan to bill this customer from that month.";
    else if (!PLAN_ID_PATTERN.test(values.planId.trim())) errors.planId = "Not a valid plan id.";
  }
  return errors;
}

export function buildPutOrgBillingSettingsRequest(values: BillingModeFormValues): PutOrgBillingSettingsRequest {
  const request: PutOrgBillingSettingsRequest = {
    effectiveMonth: values.effectiveMonth.trim(),
    billingMode: values.billingMode,
    awsPaidBy: values.awsPaidBy,
  };
  if (values.billingMode === "billed" && values.planId.trim() !== "") request.planId = values.planId.trim();
  return request;
}

export type OrgBillingSettingsState = { kind: "loaded"; settings: OrgBillingSettings } | { kind: "error"; error: ApiError };

export async function loadOrgBillingSettings(client: ApiClient, orgId: string): Promise<OrgBillingSettingsState> {
  try {
    const settings = await client.get("/v1/admin/orgs/{orgId}/billing-settings", { path: { orgId } });
    return { kind: "loaded", settings };
  } catch (error) {
    return { kind: "error", error: error as ApiError };
  }
}

export async function saveOrgBillingSettings(client: ApiClient, orgId: string, values: BillingModeFormValues): Promise<OrgBillingSettingsState> {
  try {
    const settings = await client.put("/v1/admin/orgs/{orgId}/billing-settings", {
      path: { orgId },
      body: buildPutOrgBillingSettingsRequest(values),
    });
    return { kind: "loaded", settings };
  } catch (error) {
    return { kind: "error", error: error as ApiError };
  }
}

/** A friendly message for the form's own known refusals; any other code is left to the shared
 *  `ErrorBanner` (it already prints the code and the server's message verbatim). */
export function billingModeSubmitMessage(error: ApiError): string | null {
  switch (error.code) {
    case "rate_plan_required":
      return "Choose an active plan to bill this customer from that month.";
    case "plan_not_active":
      return "That plan is not active.";
    case "period_closed":
      return "That month is already closed for this customer.";
    case "assignment_exists":
      return "That month already has a different plan assigned. Bill a later month, or keep the plan already in force.";
    default:
      return null;
  }
}

// ------------------------------------------------------------------------------ assignments

export interface AssignmentRow {
  id: string;
  effectiveMonth: string;
  planId: string;
  planName: string;
  createdBy: string;
  createdAt: string;
}

export function assignmentRow(assignment: PlanAssignment, planName: string | null, viewer?: Viewer): AssignmentRow {
  return {
    id: assignment.id,
    effectiveMonth: assignment.effectiveMonth,
    planId: assignment.planId,
    planName: planName ?? assignment.planId,
    createdBy: actorDisplay(assignment.createdBy, viewer, knownActor(assignment, "createdBy")),
    createdAt: assignment.createdAt,
  };
}

export function compareAssignmentMonth(a: AssignmentRow, b: AssignmentRow): number {
  return b.effectiveMonth.localeCompare(a.effectiveMonth);
}

/**
 * Every rate plan's name by id, so the assignments table can name a plan rather than print its id.
 * Best effort: a failed page just leaves the names found so far (the table falls back to the id).
 */
export async function loadPlanNames(client: ApiClient): Promise<ReadonlyMap<string, string>> {
  const names = new Map<string, string>();
  let cursor: string | null | undefined = null;
  try {
    // A page cap keeps a misbehaving cursor from looping forever.
    for (let page = 0; page < 50; page++) {
      const answer: components["schemas"]["RatePlanList"] = await client.get("/v1/admin/rate-plans", cursor ? { query: { cursor } } : {});
      for (const plan of answer.items) names.set(plan.id, plan.name);
      cursor = answer.nextCursor;
      if (!cursor) break;
    }
  } catch {
    // Names are a nicety; ids remain on screen.
  }
  return names;
}

export async function loadAssignments(client: ApiClient, orgId: string): Promise<{ items: readonly PlanAssignment[] } | { error: ApiError }> {
  try {
    const page = await client.get("/v1/admin/orgs/{orgId}/rate-plan-assignments", { path: { orgId } });
    return { items: page.items };
  } catch (error) {
    return { error: error as ApiError };
  }
}

// ------------------------------------------------------------------------------------ trials

/** "14-day window, $500 credit, 50M-token allowance" style summary; "no bound set" is unreachable
 *  in practice (creation requires one) but handled so the function is total. */
export function trialBoundsSummary(bounds: TrialBounds): string {
  const parts: string[] = [];
  if (bounds.startsOn && bounds.endsOn) parts.push(`${bounds.startsOn} to ${bounds.endsOn}`);
  else if (bounds.startsOn) parts.push(`from ${bounds.startsOn}`);
  else if (bounds.endsOn) parts.push(`until ${bounds.endsOn}`);
  if (bounds.creditMicroUsd !== null && bounds.creditMicroUsd !== undefined) parts.push(`${money(bounds.creditMicroUsd).text} credit`);
  if (bounds.tokenAllowance !== null && bounds.tokenAllowance !== undefined) parts.push(`${bounds.tokenAllowance.toLocaleString()} tokens`);
  if (bounds.models && bounds.models.length > 0) parts.push(bounds.models.join(", "));
  return parts.length > 0 ? parts.join(" · ") : "No bound set";
}

export interface TrialRow {
  id: string;
  statusLabel: string;
  boundsSummary: string;
  creditConsumedDisplay: MoneyDisplay;
  tokensConsumed: number;
  canCancel: boolean;
}

export function trialRow(trial: Trial, canWrite: boolean): TrialRow {
  return {
    id: trial.id,
    statusLabel: capitalize(trial.status),
    boundsSummary: trialBoundsSummary(trial.bounds),
    creditConsumedDisplay: money(trial.consumed.creditMicroUsd),
    tokensConsumed: trial.consumed.tokens,
    canCancel: canWrite && trial.status === "active",
  };
}

export async function loadTrials(client: ApiClient, orgId: string): Promise<{ items: readonly Trial[] } | { error: ApiError }> {
  try {
    const page = await client.get("/v1/admin/orgs/{orgId}/trials", { path: { orgId } });
    return { items: page.items };
  } catch (error) {
    return { error: error as ApiError };
  }
}

export interface TrialFormValues {
  startsOn: string;
  endsOn: string;
  creditUsd: string;
  tokenAllowance: string;
  models: string;
}

export function emptyTrialForm(): TrialFormValues {
  return { startsOn: "", endsOn: "", creditUsd: "", tokenAllowance: "", models: "" };
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// domain.MaxTrialCreditMicroUsd / domain.MaxTrialTokenAllowance (STATUS.md 2026-09-24): the same
// ceilings the server answers 422 `invalid_trial` past, mirrored here so a bad value never round-trips.
const MAX_TRIAL_CREDIT_MICRO_USD = 10n ** 13n; // $10,000,000
const MAX_TRIAL_TOKEN_ALLOWANCE = 10 ** 12;

/** At least one bound (window, credit or allowance) — else what the API answers 422 `trial_unbounded`. */
export function validateTrialForm(values: TrialFormValues): FieldErrors {
  const errors: FieldErrors = {};
  const hasWindow = values.startsOn.trim() !== "" || values.endsOn.trim() !== "";
  const hasCredit = values.creditUsd.trim() !== "";
  const hasAllowance = values.tokenAllowance.trim() !== "";
  if (!hasWindow && !hasCredit && !hasAllowance) {
    errors.startsOn = "Add a window, a credit or a token allowance.";
    return errors;
  }
  if (values.startsOn.trim() !== "" && !ISO_DATE.test(values.startsOn.trim())) errors.startsOn = "A date as YYYY-MM-DD.";
  if (values.endsOn.trim() !== "" && !ISO_DATE.test(values.endsOn.trim())) errors.endsOn = "A date as YYYY-MM-DD.";
  if (!errors.startsOn && !errors.endsOn && values.startsOn.trim() !== "" && values.endsOn.trim() !== "" && values.endsOn < values.startsOn) {
    errors.endsOn = "Must be on or after the start.";
  }
  if (hasCredit) {
    const shapeError = usdAmount(values.creditUsd);
    if (shapeError) errors.creditUsd = shapeError;
    else {
      const micro = parseUsd(values.creditUsd) ?? 0n;
      if (micro <= 0n) errors.creditUsd = "Must be greater than $0.";
      else if (micro > MAX_TRIAL_CREDIT_MICRO_USD) errors.creditUsd = `At most ${money(MAX_TRIAL_CREDIT_MICRO_USD).text}.`;
    }
  }
  if (hasAllowance) {
    const err = integerBetween(1, MAX_TRIAL_TOKEN_ALLOWANCE)(values.tokenAllowance);
    if (err) errors.tokenAllowance = err;
  }
  return errors;
}

export function buildCreateTrialRequest(values: TrialFormValues): CreateTrialRequest {
  const request: CreateTrialRequest = {};
  if (values.startsOn.trim() !== "") request.startsOn = values.startsOn.trim();
  if (values.endsOn.trim() !== "") request.endsOn = values.endsOn.trim();
  if (values.creditUsd.trim() !== "") request.creditMicroUsd = Number(parseUsd(values.creditUsd) ?? 0n);
  if (values.tokenAllowance.trim() !== "") request.tokenAllowance = Number(values.tokenAllowance.trim());
  const models = values.models
    .split(",")
    .map((m) => m.trim())
    .filter((m) => m !== "");
  if (models.length > 0) request.models = models;
  return request;
}

// -------------------------------------------------------------------------- burn-down (SCR-009)

export interface BurndownDayRow {
  day: string;
  priceCreditedDisplay: MoneyDisplay;
  tokensCounted: number;
  creditRemainingDisplay: MoneyDisplay | null;
  tokensRemaining: number | null;
}

export function burndownDayRow(day: BurndownDay): BurndownDayRow {
  return {
    day: day.day,
    priceCreditedDisplay: money(day.priceCreditedMicroUsd),
    tokensCounted: day.tokensCounted,
    creditRemainingDisplay: day.creditRemaining === null || day.creditRemaining === undefined ? null : money(day.creditRemaining),
    tokensRemaining: day.tokensRemaining ?? null,
  };
}

export function burndownSummary(burndown: Burndown): string {
  if (burndown.endedOn) return `Ended ${burndown.endedOn}.`;
  if (burndown.projectedEndOn) return `Projected to end ${burndown.projectedEndOn} at the current run-rate.`;
  return "No bound is projected to be reached this month.";
}

export async function loadBurndown(client: ApiClient, orgId: string, trialId: string): Promise<{ burndown: Burndown } | { error: ApiError }> {
  try {
    const burndown = await client.get("/v1/admin/orgs/{orgId}/trials/{trialId}/burndown", { path: { orgId, trialId } });
    return { burndown };
  } catch (error) {
    return { error: error as ApiError };
  }
}

// ---------------------------------------------------------------------- usage adjustments (SCR-010)

export interface AdjustmentFieldRequirements {
  amount: boolean;
  model: boolean;
  tokens: boolean;
}

/** `credit` needs an amount; `write_off` needs a model+class; `corrected_tokens` needs both plus tokens. */
export function adjustmentFieldsFor(kind: UsageAdjustmentKind): AdjustmentFieldRequirements {
  switch (kind) {
    case "credit":
      return { amount: true, model: false, tokens: false };
    case "write_off":
      return { amount: false, model: true, tokens: false };
    case "corrected_tokens":
      return { amount: false, model: true, tokens: true };
  }
}

export interface AdjustmentFormValues {
  kind: UsageAdjustmentKind;
  bedrockModelId: string;
  tokenClass: TokenClass | "";
  tokens: string;
  amountUsd: string;
  reason: string;
}

export function emptyAdjustmentForm(): AdjustmentFormValues {
  return { kind: "credit", bedrockModelId: "", tokenClass: "", tokens: "", amountUsd: "", reason: "" };
}

// domain.MaxAdjustmentAmountMicroUsd / domain.MaxAdjustmentTokens (STATUS.md 2026-09-24): the same
// ceilings the server answers 422 `invalid_adjustment` past, mirrored here so a bad value never
// round-trips.
const MAX_ADJUSTMENT_AMOUNT_MICRO_USD = 10n ** 13n; // $10,000,000
const MAX_ADJUSTMENT_TOKENS = 10 ** 12; // ± this many

export function validateAdjustmentForm(values: AdjustmentFormValues): FieldErrors {
  const errors: FieldErrors = {};
  const reasonError = lengthBetween(10, 500)(values.reason);
  if (reasonError) errors.reason = reasonError;

  const need = adjustmentFieldsFor(values.kind);
  if (need.amount) {
    if (values.amountUsd.trim() === "") errors.amountUsd = "Required.";
    else {
      const shapeError = usdAmount(values.amountUsd);
      if (shapeError) errors.amountUsd = shapeError;
      else {
        const micro = parseUsd(values.amountUsd) ?? 0n;
        if (micro <= 0n) errors.amountUsd = "Must be greater than $0.";
        else if (micro > MAX_ADJUSTMENT_AMOUNT_MICRO_USD) errors.amountUsd = `At most ${money(MAX_ADJUSTMENT_AMOUNT_MICRO_USD).text}.`;
      }
    }
  }
  if (need.model) {
    if (values.bedrockModelId.trim() === "") errors.bedrockModelId = "Required.";
    if (values.tokenClass === "") errors.tokenClass = "Required.";
  }
  if (need.tokens) {
    const text = values.tokens.trim();
    if (text === "" || !/^-?\d+$/.test(text) || Number(text) === 0) errors.tokens = "A non-zero whole number.";
    else if (Math.abs(Number(text)) > MAX_ADJUSTMENT_TOKENS) errors.tokens = `Within ±${MAX_ADJUSTMENT_TOKENS.toLocaleString()}.`;
  }
  return errors;
}

export function buildCreateAdjustmentRequest(values: AdjustmentFormValues, targetPeriod?: string): CreateUsageAdjustmentRequest {
  const request: CreateUsageAdjustmentRequest = { kind: values.kind, reason: values.reason.trim() };
  if (targetPeriod) request.targetPeriod = targetPeriod;
  const need = adjustmentFieldsFor(values.kind);
  if (need.amount) request.amountMicroUsd = Number(parseUsd(values.amountUsd) ?? 0n);
  if (need.model) {
    request.bedrockModelId = values.bedrockModelId.trim();
    if (values.tokenClass !== "") request.tokenClass = values.tokenClass;
  }
  if (need.tokens) request.tokens = Number(values.tokens.trim());
  return request;
}

/**
 * Single approver (user decision 2026-09-24 #3): any `billing:approve` holder may approve,
 * including the adjustment's own creator — this function takes no actor/subject at all, so the
 * button it gates can never be hidden from the creator.
 */
export function canApproveAdjustment(hasApprovePermission: boolean, adjustment: UsageAdjustment): boolean {
  return hasApprovePermission && adjustment.status === "draft";
}

export function canVoidAdjustment(hasWritePermission: boolean, adjustment: UsageAdjustment): boolean {
  return hasWritePermission && (adjustment.status === "draft" || adjustment.status === "approved");
}

/** Which effect an adjustment has on the price, before close — for display only (never computed
 *  into a total here): a credit and a downward correction show as negative money. */
export function adjustmentSignedMicroUsd(adjustment: UsageAdjustment): number | null {
  if (adjustment.kind === "credit" && adjustment.amountMicroUsd !== null && adjustment.amountMicroUsd !== undefined) {
    return -adjustment.amountMicroUsd;
  }
  return null;
}

export interface AdjustmentRow {
  id: string;
  kindLabel: string;
  modelClass: string;
  tokensLabel: string | null;
  amountDisplay: MoneyDisplay | null;
  reason: string;
  statusLabel: string;
  createdByLabel: string;
  approvedByLabel: string;
  voidedByLabel: string;
  canApprove: boolean;
  canVoid: boolean;
}

const KIND_LABELS: Record<UsageAdjustmentKind, string> = {
  credit: "Credit",
  write_off: "Write-off",
  corrected_tokens: "Corrected tokens",
};

export function adjustmentRow(
  adjustment: UsageAdjustment,
  canApprovePermission: boolean,
  canWritePermission: boolean,
  viewer?: Viewer,
): AdjustmentRow {
  const signed = adjustmentSignedMicroUsd(adjustment);
  return {
    id: adjustment.id,
    kindLabel: KIND_LABELS[adjustment.kind],
    modelClass: adjustment.bedrockModelId ? `${adjustment.bedrockModelId} / ${adjustment.tokenClass ?? "*"}` : "—",
    tokensLabel: adjustment.tokens === null || adjustment.tokens === undefined ? null : adjustment.tokens.toLocaleString(),
    amountDisplay: signed === null ? null : money(signed),
    reason: adjustment.reason,
    statusLabel: capitalize(adjustment.status),
    createdByLabel: actorDisplay(adjustment.createdBy, viewer, knownActor(adjustment, "createdBy")),
    approvedByLabel: actorDisplay(adjustment.approvedBy, viewer, knownActor(adjustment, "approvedBy")),
    voidedByLabel: actorDisplay(adjustment.voidedBy, viewer, knownActor(adjustment, "voidedBy")),
    canApprove: canApproveAdjustment(canApprovePermission, adjustment),
    canVoid: canVoidAdjustment(canWritePermission, adjustment),
  };
}

export async function loadAdjustments(
  client: ApiClient,
  orgId: string,
  targetPeriod?: string,
): Promise<{ items: readonly UsageAdjustment[] } | { error: ApiError }> {
  try {
    const page = await client.get("/v1/admin/orgs/{orgId}/usage-adjustments", {
      path: { orgId },
      ...(targetPeriod ? { query: { targetPeriod } } : {}),
    });
    return { items: page.items };
  } catch (error) {
    return { error: error as ApiError };
  }
}

// ------------------------------------------------------------------ the customer's Billing tab

export type ChipTone = "green" | "amber" | "grey" | "red" | "blue";
export type RatePlan = components["schemas"]["RatePlan"];
export type EffectiveRatePlan = components["schemas"]["EffectiveRatePlan"];
export type BillingPeriod = components["schemas"]["BillingPeriod"];
export type RevenueBreakdown = components["schemas"]["RevenueBreakdown"];
export type CostVisibility = components["schemas"]["CostVisibility"];

/** Status chips (Main.dc.html): active green, draft blue, needs approval amber, closed grey. */
export const TRIAL_STATUS_TONES: Readonly<Record<TrialStatus, ChipTone>> = { active: "green", ended: "grey", cancelled: "grey" };
export const ADJUSTMENT_STATUS_TONES: Readonly<Record<UsageAdjustmentStatus, ChipTone>> = {
  draft: "amber",
  approved: "green",
  applied: "grey",
  voided: "grey",
};

export const ADJUSTMENT_KIND_LABELS: Readonly<Record<UsageAdjustmentKind, string>> = KIND_LABELS;

export const TOKEN_CLASS_LABELS: Readonly<Record<TokenClass, string>> = {
  input: "Input",
  output: "Output",
  cache_read: "Cache read",
  cache_write_5m: "Cache write (5 minutes)",
  cache_write_1h: "Cache write (1 hour)",
};

export const BILLING_MODE_LABELS: Readonly<Record<BillingMode, string>> = { billed: "Billed", not_billed: "Not billed (proxy only)" };
export const AWS_PAID_BY_LABELS: Readonly<Record<AwsPaidBy, string>> = { allcode: "AllCode", client: "The customer" };
export const COST_VISIBILITY_LABELS: Readonly<Record<CostVisibility, string>> = { list_cost: "Show AWS list cost", hidden: "Hide AWS cost" };
export const COST_VISIBILITY_OPTIONS: readonly CostVisibility[] = ["list_cost", "hidden"];

/** "Who pays whom", in one sentence naming the customer (revenue-billing.md: awsPaidBy never
 *  changes what the customer is charged, only whose AWS bill the usage lands on). */
export function whoPaysWhom(settings: Pick<OrgBillingSettings, "billingMode" | "awsPaidBy">, customer: string): string {
  if (settings.billingMode === "billed") {
    return settings.awsPaidBy === "allcode"
      ? `AllCode pays AWS and invoices ${customer}.`
      : `${customer} pays its own AWS bill; AllCode invoices ${customer} on its rate plan.`;
  }
  return settings.awsPaidBy === "allcode"
    ? `AllCode pays AWS and invoices ${customer} nothing.`
    : `${customer} pays its own AWS bill and is not invoiced.`;
}

/** "Currently billed, AWS paid by AllCode." — the line the schedule's current entry reads as. */
export function currentModeLine(settings: Pick<OrgBillingSettings, "billingMode" | "awsPaidBy">): string {
  return `Currently ${isBilled(settings as OrgBillingSettings) ? "billed" : "not billed"}, AWS paid by ${settings.awsPaidBy === "allcode" ? "AllCode" : "the customer"}.`;
}

function usdPlain(micro: number): string {
  return money(micro).text.replace(/\.00$/, "");
}

/** "AWS list price plus 30 %. No fees or minimum." — a plan's pricing in one or two sentences. */
export function planSummary(plan: Pick<RatePlan, "markupBasis" | "markupBp" | "platformFeeMicroUsd" | "monthlyMinimumMicroUsd" | "rates" | "tiers">): string {
  const base = plan.markupBasis === "actual" ? "Actual AWS bill" : "AWS list price";
  const parts = [plan.markupBp === 0 ? `${base}, no markup.` : `${base} plus ${bpToPercentInput(plan.markupBp)} %.`];
  const extras: string[] = [];
  if (plan.platformFeeMicroUsd > 0) extras.push(`a ${usdPlain(plan.platformFeeMicroUsd)} monthly platform fee`);
  if (plan.monthlyMinimumMicroUsd > 0) extras.push(`a ${usdPlain(plan.monthlyMinimumMicroUsd)} monthly minimum`);
  parts.push(extras.length > 0 ? `With ${extras.join(" and ")}.` : "No fees or minimum.");
  if (plan.rates.length > 0) parts.push(`${plan.rates.length} per-model ${plan.rates.length === 1 ? "rate" : "rates"}.`);
  if (plan.tiers.length > 0) parts.push("Volume tiers.");
  return parts.join(" ");
}

/** The month the plan in force has applied since: walking back from the org's newest assignment at
 *  or before `month` over consecutive assignments of that same plan. null when the newest one is
 *  another plan (the month is priced by the fleet default) or there is none. */
export function planSince(assignments: readonly PlanAssignment[], planId: string, month: string): string | null {
  const ordered = assignments.filter((a) => a.effectiveMonth <= month).sort((a, b) => b.effectiveMonth.localeCompare(a.effectiveMonth));
  let since: string | null = null;
  for (const assignment of ordered) {
    if (assignment.planId !== planId) break;
    since = assignment.effectiveMonth;
  }
  return since;
}

/** Every rate plan (all pages); best effort, like `loadPlanNames`. */
export async function loadPlans(client: ApiClient): Promise<RatePlan[]> {
  const plans: RatePlan[] = [];
  let cursor: string | null | undefined = null;
  try {
    for (let page = 0; page < 50; page++) {
      const answer: components["schemas"]["RatePlanList"] = await client.get("/v1/admin/rate-plans", cursor ? { query: { cursor } } : {});
      plans.push(...answer.items);
      cursor = answer.nextCursor;
      if (!cursor) break;
    }
  } catch {
    // Names are a nicety; the plan form falls back to a typed plan id.
  }
  return plans;
}

/** The active plans a month can be billed on, by name. */
export function activePlans(plans: readonly RatePlan[]): RatePlan[] {
  return plans.filter((plan) => plan.status === "active").sort((a, b) => a.name.localeCompare(b.name));
}

/** Which plan prices the month (`source` org or fleet default), or null when none does or the read failed. */
export async function loadEffectivePlan(client: ApiClient, orgId: string, period?: string): Promise<EffectiveRatePlan | null> {
  try {
    return await client.get("/v1/admin/orgs/{orgId}/rate-plan", { path: { orgId }, ...(period ? { query: { period } } : {}) });
  } catch {
    return null;
  }
}

// ---- invoices by month (the Billing tab's left column) ----

export const INVOICES_SHOWN = 6;
const REVENUE_MAX_MONTHS = 36;

export function invoicePath(orgId: string, period: string): string {
  return `/customers/${encodeURIComponent(orgId)}/invoices/${encodeURIComponent(period)}`;
}

export function adjustmentsPath(orgId: string, period: string): string {
  return `${invoicePath(orgId, period)}/adjustments`;
}

function addMonthsTo(month: string, delta: number): string {
  const total = Number(month.slice(0, 4)) * 12 + Number(month.slice(5, 7)) - 1 + delta;
  const year = Math.floor(total / 12);
  return `${String(year).padStart(4, "0")}-${String(total - year * 12 + 1).padStart(2, "0")}`;
}

/** The revenue report's window for these periods: oldest to newest, at most the API's 36 months
 *  (ending at the newest); null with no periods. */
export function revenueWindow(periods: readonly Pick<BillingPeriod, "period">[]): { from: string; to: string } | null {
  if (periods.length === 0) return null;
  const months = periods.map((p) => p.period).sort();
  const to = months[months.length - 1] as string;
  const earliest = addMonthsTo(to, -(REVENUE_MAX_MONTHS - 1));
  const from = (months[0] as string) < earliest ? earliest : (months[0] as string);
  return { from, to };
}

export type InvoiceFigures =
  | { kind: "billed"; billedMicro: number; costMicro: number; profitMicro: number; provisional: boolean }
  | { kind: "not-billed" }
  | { kind: "unknown" };

export interface InvoiceRow {
  period: string;
  monthLabel: string;
  status: { label: string; tone: ChipTone };
  figures: InvoiceFigures;
  open: boolean;
  forced: boolean;
  canClose: boolean;
}

/** One row per billing period, newest first, with the month's billed / AWS cost / profit from one
 *  `/v1/admin/revenue/by-month?orgId=` report (never a call per month). */
export function invoiceRows(periods: readonly BillingPeriod[], revenue: RevenueBreakdown | null, currentMonth: string, canWrite: boolean): InvoiceRow[] {
  const byMonth = new Map((revenue?.items ?? []).map((item) => [item.key, item]));
  const notBilled = new Set((revenue?.notBilled ?? []).flatMap((item) => item.months));
  return [...periods]
    .sort((a, b) => b.period.localeCompare(a.period))
    .map((p) => {
      const open = p.status === "open";
      const item = byMonth.get(p.period);
      const figures: InvoiceFigures = item
        ? { kind: "billed", billedMicro: item.priceMicroUsd, costMicro: item.listCostMicroUsd, profitMicro: item.marginMicroUsd, provisional: item.provisional }
        : notBilled.has(p.period)
          ? { kind: "not-billed" }
          : { kind: "unknown" };
      return {
        period: p.period,
        monthLabel: longMonthLabel(p.period),
        status: open
          ? { label: p.period === currentMonth ? "Open, month to date" : "Open", tone: p.period === currentMonth ? "blue" : "amber" }
          : { label: "Closed", tone: "grey" },
        figures,
        open,
        forced: p.forced,
        canClose: canWrite && open,
      };
    });
}

// ---- trials (the Trials and credits card) ----

export function trialStatusChip(status: TrialStatus): { label: string; tone: ChipTone } {
  return { label: capitalize(status), tone: TRIAL_STATUS_TONES[status] };
}

/** "Sep 2026" for an effective month in a compact table. */
export function shortMonth(month: string): string {
  return monthLabel(month);
}
